import {
	getRecordingPermissionsAsync,
	requestRecordingPermissionsAsync,
} from "expo-audio";
import { getBrowserName, isWeb } from "./platform";

const getMobileOs = () => {
	if (typeof navigator === "undefined") {
		return undefined;
	}
	const { userAgent } = navigator;
	if (/android/i.test(userAgent)) {
		return "android";
	}
	if (/iPad|iPhone|iPod/.test(userAgent)) {
		return "ios";
	}
	return undefined;
};

export const getMicrophonePermissionHelpUrl = () => {
	const mobileOs = getMobileOs();
	switch (getBrowserName()) {
		case "Microsoft Edge":
			return "https://support.microsoft.com/en-us/windows/windows-camera-microphone-and-privacy-a83257bc-e990-d54a-d212-b5e41beba857";
		case "Firefox":
			return "https://support.mozilla.org/en-US/kb/how-manage-your-camera-and-microphone-permissions";
		case "Safari":
			return mobileOs === "ios"
				? "https://support.apple.com/guide/iphone/control-access-to-hardware-features-iph168c4bbd5/ios"
				: "https://support.apple.com/guide/safari/websites-ibrwe2159f50/mac";
		case "Opera":
			return "https://help.opera.com/en/latest/web-preferences/";
		default: {
			const chromePlatform =
				mobileOs === "android"
					? "GENIE.Platform%3DAndroid"
					: mobileOs === "ios"
						? "GENIE.Platform%3DiOS"
						: "GENIE.Platform%3DDesktop";
			return `https://support.google.com/chrome/answer/2693767?hl=en&co=${chromePlatform}`;
		}
	}
};

export const subscribeRecordingPermissionGranted = (
	onGranted: () => void,
): (() => void) | undefined => {
	if (!isWeb || typeof navigator === "undefined" || !navigator.permissions) {
		return undefined;
	}
	let status: PermissionStatus | undefined;
	let disposed = false;
	const handleChange = () => {
		if (status?.state === "granted") {
			onGranted();
		}
	};
	navigator.permissions
		.query({ name: "microphone" })
		.then((result) => {
			if (disposed) {
				return;
			}
			status = result;
			if (result.state === "granted") {
				onGranted();
				return;
			}
			result.addEventListener("change", handleChange);
		})
		.catch(() => {
			// Permissions API can't watch the microphone in this browser; the
			// explicit request CTA remains the fallback.
		});
	return () => {
		disposed = true;
		status?.removeEventListener("change", handleChange);
	};
};

export const isRecordingPermissionDenied = async () => {
	try {
		const { status } = await getRecordingPermissionsAsync();
		return status === "denied";
	} catch {
		return false;
	}
};

export const requestRecordingPermission = async () => {
	try {
		if (!isWeb) {
			const existing = await getRecordingPermissionsAsync();
			if (existing?.status === "granted") {
				return true;
			}
		}

		const next = await requestRecordingPermissionsAsync();
		return next.status === "granted";
	} catch {
		return false;
	}
};
